import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'first_name', label: 'Legal First Name', required: true },
  { key: 'last_name', label: 'Legal Last Name', required: true },
  { key: 'dob', label: 'Date of Birth (MM/DD/YYYY)', required: true, placeholder: 'MM/DD/YYYY' },
  { key: 'street_address', label: 'Home Street Address', required: true, full: true },
  { key: 'apt_suite', label: 'Apartment, Suite, Room' },
  { key: 'city', label: 'City', required: true },
  { key: 'state', label: 'State', required: true },
  { key: 'zip', label: 'ZIP', required: true },
  { key: 'ssn', label: 'Social Security #', required: true, placeholder: 'XXX-XX-XXXX' },
];

const JumboOwnerInfoPublicForm = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({});

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/shared/jumbo-owner-info/${token}`)
      .then(res => {
        setInfo(res.data);
        // Pre-fill with any existing data the broker entered
        const o = res.data?.owner || {};
        setData({
          first_name: o.first_name || '',
          last_name: o.last_name || '',
          dob: o.dob || '',
          street_address: o.street_address || '',
          apt_suite: o.apt_suite || '',
          city: o.city || '',
          state: o.state || '',
          zip: o.zip || '',
          ssn: o.ssn || '',
        });
        setLoading(false);
      })
      .catch(() => { setError('This link is invalid or has expired.'); setLoading(false); });
  }, [token]);

  const handleSubmit = async () => {
    const missing = FIELDS.filter(f => f.required && !data[f.key]).map(f => f.label);
    if (missing.length) { alert(`Please fill in: ${missing.join(', ')}`); return; }
    setSubmitting(true);
    try {
      await axios.put(`${BACKEND_URL}/api/shared/jumbo-owner-info/${token}`, data);
      setSubmitted(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string'
        ? d
        : Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join('; ')
        : 'Submission failed. Please try again.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white border border-red-200 rounded-xl p-8 max-w-md text-center">
        <p className="text-red-700 font-semibold">{error}</p>
      </div>
    </div>
  );
  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white border border-emerald-200 rounded-xl p-10 max-w-md text-center">
        <CheckCircle className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-800">Thank you</h2>
        <p className="text-slate-600 mt-2">Your owner information has been submitted to the broker. You may close this window.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="jumbo-owner-info-page">
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#081120] text-white rounded-t-xl px-6 py-5">
          <h1 className="text-xl font-bold">Quidity Direct — Owner Information</h1>
          <p className="text-sm text-slate-300 mt-1">
            Application for <strong>{info?.business_legal_name || 'the business'}</strong>
          </p>
        </div>
        <div className="bg-white border border-slate-200 border-t-0 rounded-b-xl p-6 space-y-5">
          <p className="text-sm text-slate-600">
            You&apos;ve been listed as an additional owner with a 20%+ stake. Please fill in your owner information below — the broker will use this to complete the application.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {FIELDS.map(f => (
              <div key={f.key} className={f.full ? 'md:col-span-3' : ''}>
                <Label htmlFor={`owner-${f.key}`} className="text-xs uppercase font-semibold text-slate-700">
                  {f.label}{f.required && <span className="text-red-500"> *</span>}
                </Label>
                <Input
                  id={`owner-${f.key}`}
                  value={data[f.key] || ''}
                  onChange={e => setData({ ...data, [f.key]: e.target.value })}
                  placeholder={f.placeholder || ''}
                  data-testid={`owner-info-field-${f.key}`}
                  className="mt-1"
                />
              </div>
            ))}
          </div>
          <div className="pt-4 border-t border-slate-200 flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#0E333F] hover:bg-[#072028] text-white"
              data-testid="owner-info-submit"
            >
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : 'Submit Owner Info'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JumboOwnerInfoPublicForm;
